import PropTypes from 'prop-types'
import { useState } from 'react'
import styled, { css } from 'styled-components'
import Textarea from 'react-textarea-autosize'

import colors from 'components/utils/colors'
import IconLink from 'components/Shared/IconLink/Index'

const PADDING_VERT = 7
const PADDING_HORIZ_SMALL = 7
const PADDING_HORIZ_BIG = 15
const BORDER_SIZE = 1
const ADDITIONAL_HEIGHT = PADDING_VERT * 2 + BORDER_SIZE * 2
const CLEAR_ICON_PADDING_SMALL = 12
const CLEAR_ICON_PADDING_BIG = 16
const CLEAR_ICON_SIZE = 9
const CLEAR_ICON_SIZE_SMALL = CLEAR_ICON_SIZE + CLEAR_ICON_PADDING_SMALL * 2
const CLEAR_ICON_SIZE_BIG = CLEAR_ICON_SIZE + CLEAR_ICON_PADDING_BIG * 2

const StyledWrapper = styled.div`
  position: relative;
`

const styleAsDisabled = css`
  background-color: ${colors.secondary95};
`

const styleEnableResize = css`
  resize: vertical;
`

const styledAsError = css`
  && {
    border: ${BORDER_SIZE}px solid ${colors.alert};
  }
`

const styleAsBig = css`
  padding: ${PADDING_VERT}px ${CLEAR_ICON_SIZE_BIG}px ${PADDING_VERT}px ${PADDING_HORIZ_BIG}px;
  line-height: 20px;
  min-height: 40px;
`

// paddings are 7px according to spec (8 px including border)
// total height is 32px = 16 (text) + 7*2 (paddings without border) + 1*2 (borders)
const styleAsSmall = css`
  padding: ${PADDING_VERT}px ${CLEAR_ICON_SIZE_SMALL}px ${PADDING_VERT}px ${PADDING_HORIZ_SMALL}px;
  line-height: 16px;
  min-height: 32px;
`

const StyledTextArea = styled(Textarea)`
  width: ${props => props.width || '100%'};
  box-sizing: border-box;
  resize: none;
  border-radius: 3px;
  border: ${BORDER_SIZE}px solid ${colors.secondary70};
  outline: none;
  font-size: 14px;
  transition: width 0.5s;

  &:focus {
    border-color: ${colors.primary40};
  }

  &::placeholder {
    color: ${colors.secondary70};
  }

  ${props => props.disabled && styleAsDisabled}
  ${props => props['data-enable-resize'] && styleEnableResize}
  ${props => props['data-is-error'] && styledAsError}
  ${props => props['data-small'] ? styleAsSmall : styleAsBig}
`

const StyledClearButton = styled(IconLink)`
  position: absolute;
  right: 0;
  padding: ${props => props.$small ? CLEAR_ICON_PADDING_SMALL : CLEAR_ICON_PADDING_BIG}px;
`

const DynamicTextArea = ({
  value, onChange, disabled, placeholder, minRows = 1,
  maxRows, defaultValue, isError, small, width, domNodeRef, autoFocus, className,
  showClear = true,
}) => {
  const [enableResize, setEnableResize] = useState(Boolean(minRows > 1))

  const onHeightChange = (height, { rowHeight }) => {
    const newEnableResize = Boolean(height > rowHeight + ADDITIONAL_HEIGHT)
    setEnableResize(newEnableResize)
  }

  const onClear = () => {
    const fakeEvent = {
      target: {
        value: '',
      },
    }
    onChange(fakeEvent)
  }

  return (
    <StyledWrapper className={className}>
      <StyledTextArea
        autoFocus={autoFocus}
        defaultValue={defaultValue}
        value={value || ''}
        onChange={onChange}
        disabled={disabled}
        placeholder={placeholder || ''}
        minRows={minRows}
        maxRows={maxRows}
        data-enable-resize={enableResize}
        data-is-error={isError}
        data-small={small}
        onHeightChange={onHeightChange}
        width={width}
        ref={domNodeRef}
      />
      {!!value && showClear && !disabled && (
        <StyledClearButton
          $small={small}
          iconName='icon-clear-search'
          onClick={onClear}
        />
      )}
    </StyledWrapper>
  )
}

DynamicTextArea.propTypes = {
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.array, // On ticket edit page, partner ticket id can be array for power user.
  ]),
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  minRows: PropTypes.number,
  maxRows: PropTypes.number,
  defaultValue: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
  ]),
  isError: PropTypes.bool,
  small: PropTypes.bool,
  width: PropTypes.string,
  domNodeRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.any }),
  ]),
  autoFocus: PropTypes.bool,
  showClear: PropTypes.bool,
}

export default DynamicTextArea
